export interface HelpEntry {
  key: string;
  route: string;
  tab?: string;
  tourSceneId?: string;
  links?: string[];
}

export function helpKeyFor(route: string, tab?: string): string {
  const slug = route.replace(/^\//, '').replace(/\//g, '.');
  return tab ? `${slug}.tab.${tab}` : slug;
}

function entry(route: string, options: { tab?: string; tour?: string; links?: string[] } = {}): HelpEntry {
  return {
    key: helpKeyFor(route, options.tab),
    route,
    ...(options.tab ? { tab: options.tab } : {}),
    ...(options.tour ? { tourSceneId: options.tour } : {}),
    ...(options.links ? { links: options.links } : {}),
  };
}

export const HELP_ENTRIES: readonly HelpEntry[] = [
  entry('/dashboard', { tour: 'opd-start-dashboard', links: ['/reports', '/queue', '/billing'] }),
  entry('/queue', { tour: 'opd-clinic-vitals', links: ['/appointments', '/triage', '/consultations'] }),
  entry('/triage', { links: ['/queue', '/appointments'] }),
  entry('/cosign', { links: ['/consultations', '/queue'] }),
  entry('/appointments', { tour: 'opd-clinic-book', links: ['/schedules', '/queue', '/patients/new', '/follow-ups'] }),
  entry('/schedules', { tour: 'opd-clinic-hours', links: ['/appointments', '/people'] }),
  entry('/follow-ups', { links: ['/appointments', '/patients'] }),
  entry('/online-requests', { links: ['/appointments', '/admin/listing', '/lab'] }),
  entry('/reports', { tour: 'opd-admin-reports', links: ['/dashboard', '/billing', '/lab/reports'] }),
  entry('/patients', { tour: 'opd-patients-search', links: ['/patients/new', '/appointments', '/billing'] }),
  entry('/patients/new', { tour: 'opd-patients-register', links: ['/patients', '/appointments'] }),
  entry('/mrd', { links: ['/patients', '/consultations', '/ipd/admissions'] }),
  entry('/consultations', { tour: 'opd-clinic-note', links: ['/queue', '/cosign', '/lab', '/pharmacy', '/billing'] }),
  entry('/ipd/beds', { tour: 'ipd-beds', links: ['/ipd/admissions', '/housekeeping', '/admin/branches'] }),
  entry('/ipd/admissions', { tour: 'ipd-admit', links: ['/ipd/beds', '/ipd/charge-rules', '/ipd/dues', '/billing'] }),
  entry('/ipd/wards', { links: ['/ipd/admissions', '/ipd/beds'] }),
  entry('/ipd/locator', { links: ['/ipd/beds', '/ipd/admissions'] }),
  entry('/ipd/charge-rules', { links: ['/billing/charges', '/ipd/admissions'] }),
  entry('/er', { links: ['/triage', '/ipd/admissions', '/patients/new'] }),
  entry('/ot', { links: ['/ipd/admissions', '/billing/charges'] }),
  entry('/blood-bank', { links: ['/lab', '/ipd/admissions'] }),
  entry('/maternity', { links: ['/ipd/admissions', '/patients/new'] }),
  entry('/therapy', { links: ['/appointments', '/billing', '/billing/charges'] }),
  entry('/dental', { links: ['/appointments', '/consultations', '/billing/charges'] }),
  entry('/lab', { tour: 'lab-order', links: ['/lab/catalogue', '/lab/critical', '/lab/home-collection', '/billing'] }),
  entry('/lab/critical', { links: ['/lab', '/notifications'] }),
  entry('/lab/home-collection', { links: ['/lab', '/online-requests'] }),
  entry('/lab/qc', { links: ['/lab', '/lab/catalogue'] }),
  entry('/lab/catalogue', { tour: 'lab-catalogue', links: ['/lab', '/billing/charges'] }),
  entry('/lab/reports', { links: ['/lab', '/reports'] }),
  entry('/radiology', { links: ['/consultations', '/billing', '/lab/catalogue'] }),
  entry('/pharmacy', { tour: 'store-sale', links: ['/inventory', '/billing/shift', '/stock-control'] }),
  entry('/inventory', { tour: 'store-products', links: ['/pharmacy', '/stock-control', '/payables', '/assets'] }),
  entry('/stock-control', { links: ['/inventory', '/pharmacy'] }),
  entry('/online-orders', { links: ['/pharmacy', '/admin/listing'] }),
  entry('/billing', { tour: 'opd-billing-invoices', links: ['/billing/shift', '/billing/refunds', '/billing/charges', '/insurance'] }),
  entry('/billing/opd-dues', { links: ['/billing', '/queue', '/billing/shift'] }),
  entry('/billing/shift', { tour: 'opd-billing-shift', links: ['/billing', '/billing/settings', '/reports'] }),
  entry('/billing/refunds', { links: ['/billing', '/accounts/authority'] }),
  entry('/billing/credit-customers', { links: ['/billing', '/pharmacy'] }),
  entry('/ipd/dues', { links: ['/ipd/admissions', '/billing'] }),
  entry('/billing/charges', { links: ['/billing', '/accounts/doctor-share', '/lab/catalogue', '/insurance'] }),
  entry('/billing/settings', { links: ['/billing/shift', '/accounts/authority', '/admin/settings'] }),
  entry('/insurance', { links: ['/billing', '/patients', '/billing/charges'] }),
  entry('/accounting', { links: ['/accounting/payouts', '/billing', '/payables'] }),
  entry('/payables', { links: ['/inventory', '/accounting'] }),
  entry('/accounts/authority', { links: ['/billing/refunds', '/billing', '/people'] }),
  entry('/accounts/doctor-share', { links: ['/accounting/payouts', '/billing/charges'] }),
  entry('/accounting/payouts', { links: ['/accounts/doctor-share', '/accounting'] }),
  entry('/ambulance', { links: ['/er', '/billing'] }),
  entry('/dietary', { links: ['/ipd/admissions', '/ipd/beds'] }),
  entry('/housekeeping', { links: ['/ipd/beds', '/tasks'] }),
  entry('/tasks', { links: ['/housekeeping', '/notifications'] }),
  entry('/notifications', { links: ['/appointments', '/lab/critical', '/admin/settings'] }),
  entry('/feedback', { links: ['/tasks', '/patients'] }),
  entry('/assets', { links: ['/inventory', '/tasks'] }),
  entry('/people', { tour: 'opd-admin-team', links: ['/admin/branches', '/admin/settings', '/admin/audit', '/schedules', '/accounting'] }),
  entry('/admin/branches', { links: ['/people', '/admin/settings', '/ipd/beds'] }),
  entry('/admin/settings', { tour: 'opd-admin-settings', links: ['/people', '/admin/branches', '/billing/settings', '/admin/listing'] }),
  entry('/admin/listing', { links: ['/online-requests', '/online-orders', '/admin/branches'] }),
  entry('/admin/audit', { tour: 'opd-admin-audit', links: ['/people', '/admin/settings'] }),
  entry('/support', { links: ['/admin/settings'] }),
  entry('/account/security', { links: ['/support'] }),
  entry('/people', { tab: 'people', tour: 'opd-admin-team', links: ['/schedules', '/accounts/authority'] }),
  entry('/people', { tab: 'roles', links: ['/admin/settings'] }),
  entry('/people', { tab: 'roster', links: ['/schedules'] }),
  entry('/people', { tab: 'attendance', links: ['/schedules'] }),
  entry('/people', { tab: 'leave', links: ['/schedules', '/appointments'] }),
  entry('/people', { tab: 'payroll', links: ['/accounting', '/accounts/authority'] }),
  entry('/billing/charges', { tab: 'charges', links: ['/accounts/doctor-share', '/lab/catalogue'] }),
  entry('/billing/charges', { tab: 'tariffs', links: ['/insurance', '/patients'] }),
  entry('/billing/charges', { tab: 'packages', links: ['/billing'] }),
  entry('/billing/charges', { tab: 'consultation', links: ['/appointments', '/queue'] }),
  entry('/insurance', { tab: 'claims', links: ['/billing'] }),
  entry('/insurance', { tab: 'aging', links: ['/accounting'] }),
  entry('/insurance', { tab: 'reports', links: ['/reports'] }),
  entry('/insurance', { tab: 'preauth', links: ['/consultations', '/ipd/admissions'] }),
  entry('/insurance', { tab: 'payments', links: ['/accounting', '/billing'] }),
  entry('/insurance', { tab: 'requests', links: ['/consultations'] }),
  entry('/insurance', { tab: 'payers', links: ['/patients', '/billing/charges'] }),
  entry('/inventory', { tab: 'products', tour: 'store-products', links: ['/pharmacy', '/billing/charges'] }),
  entry('/inventory', { tab: 'stock', tour: 'store-after-sale', links: ['/stock-control'] }),
  entry('/inventory', { tab: 'expiry', tour: 'store-control', links: ['/payables', '/stock-control'] }),
  entry('/inventory', { tab: 'reorder', tour: 'store-control', links: ['/payables'] }),
  entry('/inventory', { tab: 'valuation', tour: 'store-control', links: ['/reports', '/accounting'] }),
  entry('/inventory', { tab: 'suppliers', tour: 'store-supplier', links: ['/payables'] }),
  entry('/inventory', { tab: 'purchase-orders', links: ['/payables'] }),
  entry('/inventory', { tab: 'goods-received', tour: 'store-receive', links: ['/payables', '/stock-control'] }),
  entry('/inventory', { tab: 'indents', links: ['/ipd/admissions', '/pharmacy'] }),
];

export function findHelpEntry(route: string, tab?: string): HelpEntry | undefined {
  return HELP_ENTRIES.find((candidate) => candidate.route === route && (tab ? candidate.tab === tab : !candidate.tab));
}

export function findHelpEntryByKey(key: string): HelpEntry | undefined {
  return HELP_ENTRIES.find((candidate) => candidate.key === key);
}
